import React, { useState } from 'react';
import FilterPanel from './components/FilterPanel';
import ReportTable from './components/ReportTable';
import ChartDashboard from './components/ChartDashboard';
import Charts2Dashboard from './components/Charts2Dashboard';
import ExportButton from './components/ExportButton';
import { FilterParams, AuditLog, REPORT_TYPES } from './types/audit';
import auditAPI, { Environment } from './services/api';

function App() {
  const [environment, setEnvironment] = useState<Environment>('prod');
  const [view, setView] = useState<'table' | 'charts' | 'charts2'>('table');
  const [data, setData] = useState<AuditLog[]>([]);
  const [filters, setFilters] = useState<FilterParams | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFilterChange = async (newFilters: FilterParams) => {
    setFilters(newFilters);
    setLoading(true);
    setError(null);

    try {
      const response = await auditAPI.queryAudits(newFilters, environment);
      setData(response.data);
    } catch (err: any) {
      console.error('Query failed:', err);
      setError(err.message || 'Failed to load audit logs. Please try again.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickReport = async (reportId: string) => {
    setLoading(true);
    setError(null);
    setView('table');

    try {
      // Use current date range or default to last 7 days
      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      
      const quickFilters: FilterParams = {
        dateRange: {
          start: filters?.dateRange?.start || startDate,
          end: filters?.dateRange?.end || endDate,
        }
      };

      const response = await auditAPI.queryByType(reportId, quickFilters, environment);
      setData(response.data);
      setFilters(quickFilters);
    } catch (err: any) {
      console.error('Quick report failed:', err);
      setError(err.message || 'Failed to load report. Please try again.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const isProd = environment === 'prod';

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.headerLeft}>
            <div style={styles.headerTitleRow}>
              <svg style={styles.headerIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 17H7A5 5 0 0 1 7 7h2"/>
                <path d="M15 7h2a5 5 0 0 1 0 10h-2"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              <h1 style={styles.title}>Audit Reports</h1>
            </div>
            <p style={styles.subtitle}>Triage TextGen AI &mdash; Query and analyze audit logs from Amazon Athena</p>
          </div>
          <div style={styles.headerRight}>
            <button
              onClick={() => setEnvironment(isProd ? 'dev' : 'prod')}
              style={{
                ...styles.envToggle,
                backgroundColor: isProd ? '#1a7f4b' : '#2563eb',
              }}
              title={`Switch to ${isProd ? 'Development' : 'Production'}`}
            >
              <span style={styles.envDot} />
              {isProd ? 'Production' : 'Development'}
              <span style={styles.envRegion}>{isProd ? 'eu-west-1' : 'ap-southeast-2'}</span>
            </button>
            <div style={styles.logoPill}>
              <img
                src="/infomedia-logo.png"
                alt="Infomedia"
                style={styles.logo}
              />
            </div>
          </div>
        </div>
      </header>

      <div style={styles.container}>
        <aside style={styles.sidebar}>
          <FilterPanel onFilterChange={handleFilterChange} loading={loading} environment={environment} />

          <div style={styles.reportTypes}>
            <div style={styles.sectionLabel}>Quick Reports</div>
            {REPORT_TYPES.map((report) => (
              <button
                key={report.id}
                style={styles.reportTypeButton}
                title={report.description}
                onClick={() => handleQuickReport(report.id)}
                disabled={loading}
              >
                <span style={styles.reportTypeIcon}>{report.icon}</span>
                <span style={styles.reportTypeName}>{report.name}</span>
              </button>
            ))}
          </div>
        </aside>

        <main style={styles.main}>
          <div style={styles.toolbar}>
            <div style={styles.viewToggle}>
              <button
                style={{
                  ...styles.viewButton,
                  ...(view === 'table' ? styles.viewButtonActive : {}),
                }}
                onClick={() => setView('table')}
              >
                Table View
              </button>
              <button
                style={{
                  ...styles.viewButton,
                  ...(view === 'charts' ? styles.viewButtonActive : {}),
                }}
                onClick={() => setView('charts')}
              >
                Charts
              </button>
              <button
                style={{
                  ...styles.viewButton,
                  ...(view === 'charts2' ? styles.viewButtonActive : {}),
                }}
                onClick={() => setView('charts2')}
              >
                Charts 2.0
              </button>
            </div>

            {filters && data.length > 0 && (
              <ExportButton filters={filters} data={data} />
            )}
          </div>

          {error && (
            <div style={styles.error}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {view === 'table' ? (
            <>
              {data.length > 0 && (
                <div style={styles.resultsSummary}>
                  <span style={styles.resultsBadge}>{data.length}</span>
                  &nbsp;result{data.length !== 1 ? 's' : ''} found
                </div>
              )}
              <ReportTable data={data} loading={loading} />
            </>
          ) : view === 'charts' ? (
            <ChartDashboard dateRange={filters?.dateRange} environment={environment} />
          ) : (
            <Charts2Dashboard dateRange={filters?.dateRange} environment={environment} />
          )}
        </main>
      </div>

      <footer style={styles.footer}>
        <span>Audit Reports v1.0.0</span>
        <span style={styles.footerDivider}>|</span>
        <span>Triage TextGen AI</span>
        <span style={styles.footerDivider}>|</span>
        <span>&copy; {new Date().getFullYear()} Infomedia Ltd</span>
      </footer>
    </div>
  );
}

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  navy:        '#0d1f3c',
  navyLight:   '#162a4a',
  blue:        '#0055a5',
  blueLight:   '#e8f0fb',
  bg:          '#eef1f6',
  surface:     '#ffffff',
  border:      '#d8dde6',
  textPrimary: '#1a2332',
  textMuted:   '#5a6a80',
  success:     '#1a7f4b',
  error:       '#b91c1c',
  errorBg:     '#fef2f2',
  errorBorder: '#fca5a5',
};

const styles: { [key: string]: React.CSSProperties } = {
  app: {
    minHeight: '100vh',
    backgroundColor: C.bg,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
  },

  // ── Header ────────────────────────────────────────────────────────────────
  header: {
    backgroundColor: C.navy,
    color: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '1600px',
    margin: '0 auto',
    padding: '0 24px',
    height: '64px',
    width: '100%',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  headerTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerIcon: {
    width: '20px',
    height: '20px',
    color: '#6ba3e0',
    flexShrink: 0,
  } as React.CSSProperties,
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  envToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.02em',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  envDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.8)',
    flexShrink: 0,
  },
  envRegion: {
    fontSize: '10px',
    opacity: 0.7,
    fontWeight: '400',
    marginLeft: '2px',
  },
  logoPill: {
    backgroundColor: 'white',
    borderRadius: '6px',
    padding: '4px 10px',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
  },
  logo: {
    height: '26px',
    objectFit: 'contain',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    letterSpacing: '-0.01em',
    color: 'white',
  },
  subtitle: {
    margin: '2px 0 0 0',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.55)',
    letterSpacing: '0.02em',
    paddingLeft: '30px',
  },

  // ── Layout ────────────────────────────────────────────────────────────────
  container: {
    display: 'flex',
    flex: 1,
    maxWidth: '1600px',
    margin: '0 auto',
    width: '100%',
    padding: '24px',
    gap: '20px',
    alignItems: 'flex-start',
  },
  sidebar: {
    width: '290px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  main: {
    flex: 1,
    minWidth: 0,
  },

  // ── Toolbar ───────────────────────────────────────────────────────────────
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  viewToggle: {
    display: 'flex',
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: '6px',
    padding: '3px',
    gap: '2px',
  },
  viewButton: {
    padding: '6px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: C.textMuted,
    transition: 'all 0.15s',
    letterSpacing: '0.01em',
  },
  viewButtonActive: {
    backgroundColor: C.blue,
    color: 'white',
    boxShadow: '0 1px 3px rgba(0,85,165,0.3)',
  },

  // ── Results summary ───────────────────────────────────────────────────────
  resultsSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: C.blueLight,
    borderLeft: `3px solid ${C.blue}`,
    borderRadius: '0 4px 4px 0',
    marginBottom: '14px',
    fontSize: '13px',
    color: C.textPrimary,
    fontWeight: '500',
  },
  resultsBadge: {
    backgroundColor: C.blue,
    color: 'white',
    borderRadius: '10px',
    padding: '1px 8px',
    fontSize: '12px',
    fontWeight: '600',
  },

  // ── Error ─────────────────────────────────────────────────────────────────
  error: {
    padding: '12px 16px',
    backgroundColor: C.errorBg,
    color: C.error,
    borderRadius: '6px',
    marginBottom: '16px',
    border: `1px solid ${C.errorBorder}`,
    fontSize: '13px',
  },

  // ── Quick Reports ─────────────────────────────────────────────────────────
  reportTypes: {
    backgroundColor: C.surface,
    borderRadius: '8px',
    border: `1px solid ${C.border}`,
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: C.textMuted,
    marginBottom: '10px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${C.border}`,
  },
  reportTypeButton: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '9px 10px',
    marginBottom: '4px',
    border: `1px solid transparent`,
    backgroundColor: 'transparent',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '13px',
    textAlign: 'left' as const,
    transition: 'all 0.15s',
    color: C.textPrimary,
  },
  reportTypeIcon: {
    marginRight: '9px',
    fontSize: '15px',
    opacity: 0.8,
  },
  reportTypeName: {
    fontSize: '13px',
    fontWeight: '500',
  },

  // ── Footer ────────────────────────────────────────────────────────────────
  footer: {
    backgroundColor: C.navy,
    borderTop: `1px solid ${C.navyLight}`,
    padding: '14px 24px',
    textAlign: 'center' as const,
    fontSize: '12px',
    color: 'rgba(255,255,255,0.45)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
  },
  footerDivider: {
    opacity: 0.3,
  },
};

export default App;

